import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { getSecurityOverview } from "@/lib/security.functions";
import { AdminSection } from "./admin";


export const Route = createFileRoute("/_authenticated/admin/security")({
  head: () => ({
    meta: [
      { title: "Security · Admin · Buddha KA" },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: SecurityDashboard,
});

const SEVERITY_STYLES: Record<string, string> = {
  critical: "bg-red-500/10 text-red-400 border-red-500/30",
  warn: "bg-yellow-500/10 text-yellow-300 border-yellow-500/30",
  info: "bg-slate-500/10 text-slate-300 border-slate-500/30",
};

function SecurityDashboard() {
  const fetchOverview = useServerFn(getSecurityOverview);
  const { data, isLoading, error, refetch, isFetching } = useQuery({
    queryKey: ["admin", "security", "overview"],
    queryFn: () => fetchOverview(),
    refetchInterval: 60_000,
    staleTime: 30_000,
  });

  return (
    <AdminSection
      title="Security signals"
      description="Auth, permission, and push-subscription anomalies detected by the scanner. Refreshes every minute."
      action={
        <div className="flex flex-wrap gap-2">
          <Link
            to="/admin/security/settings"
            className="rounded-full border hairline px-4 py-1.5 text-xs uppercase tracking-[0.2em] text-muted-foreground hover:text-foreground"
          >
            Settings
          </Link>
          <button
            onClick={() => refetch()}
            className="rounded-full border hairline px-4 py-1.5 text-xs uppercase tracking-[0.2em] text-muted-foreground hover:text-foreground disabled:opacity-50"
            disabled={isFetching}
          >
            {isFetching ? "Refreshing…" : "Refresh"}
          </button>
        </div>
      }
    >
      {isLoading ? (
        <p className="text-sm text-muted-foreground">Loading signals…</p>
      ) : error ? (
        <p className="text-sm text-red-400">
          Failed to load signals: {(error as Error).message}
        </p>
      ) : !data ? null : (
        <div className="space-y-8">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <Stat label="Critical (7d)" value={data.totals.critical} tone="critical" />
            <Stat label="Warnings (7d)" value={data.totals.warn} tone="warn" />
            <Stat label="Info (7d)" value={data.totals.info} tone="info" />
          </div>

          <div>
            <h3 className="text-xs uppercase tracking-[0.2em] text-muted-foreground">
              Events per day
            </h3>
            <div className="mt-4 flex items-end gap-2">
              {data.buckets.map((b) => {
                const total = b.critical + b.warn + b.info;
                const max = Math.max(1, ...data.buckets.map((x) => x.critical + x.warn + x.info));
                const h = Math.max(4, Math.round((total / max) * 96));
                return (
                  <div key={b.day} className="flex flex-1 flex-col items-center gap-1">
                    <div
                      className="flex w-full flex-col-reverse overflow-hidden rounded-md bg-slate-800/40"
                      style={{ height: h }}
                      title={`${b.day} · ${total} total`}
                    >
                      {b.critical ? (
                        <div className="bg-red-500/70" style={{ flex: b.critical }} />
                      ) : null}
                      {b.warn ? (
                        <div className="bg-yellow-500/70" style={{ flex: b.warn }} />
                      ) : null}
                      {b.info ? (
                        <div className="bg-slate-500/60" style={{ flex: b.info }} />
                      ) : null}
                    </div>
                    <span className="text-[10px] text-muted-foreground">
                      {b.day.slice(5)}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>

          <div>
            <h3 className="text-xs uppercase tracking-[0.2em] text-muted-foreground">
              Top rules (7d)
            </h3>
            {data.topRules.length === 0 ? (
              <p className="mt-3 text-sm text-muted-foreground">
                No rules have tripped in the last 7 days. The scanner heartbeat only will
                appear here once cron is wired.
              </p>
            ) : (
              <ul className="mt-3 divide-y hairline rounded-lg border hairline">
                {data.topRules.map((r) => {
                  const st = data.state.find((s) => s.rule_key === r.rule_key);
                  return (
                    <li
                      key={r.rule_key}
                      className="flex items-center justify-between gap-3 px-4 py-3 text-sm"
                    >
                      <div className="min-w-0 flex-1">
                        <div className="truncate font-mono text-xs">{r.rule_key}</div>
                        {st?.last_fired_at ? (
                          <div className="text-[11px] text-muted-foreground">
                            Last alert {new Date(st.last_fired_at).toLocaleString()}
                            {st.last_severity ? ` · ${st.last_severity}` : ""}
                          </div>
                        ) : null}
                      </div>
                      <span className="text-xs text-muted-foreground">{r.count}</span>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          <div>
            <h3 className="text-xs uppercase tracking-[0.2em] text-muted-foreground">
              Recent events
            </h3>
            {data.rows.length === 0 ? (
              <p className="mt-3 text-sm text-muted-foreground">No recent events.</p>
            ) : (
              <div className="mt-3 overflow-hidden rounded-lg border hairline">
                <table className="w-full text-left text-xs">
                  <thead className="bg-slate-900/40 text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                    <tr>
                      <th className="px-3 py-2">When</th>
                      <th className="px-3 py-2">Severity</th>
                      <th className="px-3 py-2">Rule</th>
                      <th className="px-3 py-2">Subject</th>
                      <th className="px-3 py-2 text-right">Count</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y hairline">
                    {data.rows.slice(0, 50).map((row) => (
                      <tr key={row.id}>
                        <td className="px-3 py-2 text-muted-foreground">
                          {new Date(row.created_at).toLocaleString()}
                        </td>
                        <td className="px-3 py-2">
                          <span
                            className={`rounded-full border px-2 py-0.5 text-[10px] uppercase tracking-[0.2em] ${SEVERITY_STYLES[row.severity] ?? ""}`}
                          >
                            {row.severity}
                          </span>
                        </td>
                        <td className="px-3 py-2 font-mono">{row.rule_key}</td>
                        <td className="px-3 py-2 text-muted-foreground">
                          {row.subject ?? "—"}
                        </td>
                        <td className="px-3 py-2 text-right">{row.event_count}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <details className="rounded-lg border hairline p-4 text-xs text-muted-foreground">
            <summary className="cursor-pointer text-foreground">
              How to run the scanner
            </summary>
            <p className="mt-2">
              The scanner is exposed as a signed POST endpoint at{" "}
              <code className="font-mono">/api/public/hooks/security-scan</code> and is
              designed to be pinged by pg_cron every 15 minutes.
            </p>
            <p className="mt-2">Request headers:</p>
            <pre className="mt-1 whitespace-pre-wrap font-mono text-[11px]">
{`POST /api/public/hooks/security-scan?minutes=15
Content-Type: application/json
x-security-scan-secret: <SECURITY_SCAN_SECRET>
`}
            </pre>
            <p className="mt-2">
              Add <code>?dry_run=1</code> to preview signals without inserting rows or
              sending admin email.
            </p>
          </details>
        </div>
      )}
    </AdminSection>
  );
}

function Stat({
  label,
  value,
  tone,
}: {
  label: string;
  value: number;
  tone: "critical" | "warn" | "info";
}) {
  return (
    <div
      className={`rounded-xl border hairline px-5 py-4 ${
        tone === "critical"
          ? "bg-red-500/5"
          : tone === "warn"
            ? "bg-yellow-500/5"
            : "bg-slate-500/5"
      }`}
    >
      <p className="text-[11px] uppercase tracking-[0.25em] text-muted-foreground">
        {label}
      </p>
      <p className="mt-2 font-serif text-3xl">{value}</p>
    </div>
  );
}
